import type React from 'react'
import type { JSX, MouseEvent } from 'react'
import type { IconNames, IconSizes } from 'atoms/Icon'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { COLOR } from 'vars/index'
import type { DropdownItemPadding } from '../DropdownItem.h'

import { Icon } from 'atoms/Icon'
import { TextAligning, TextBox } from 'atoms/TextBox'
import {
  DropdownItemBackgrounds,
  DropdownItemColumnGap,
  DropdownItemFontColors,
  DropdownItemFonts,
  DropdownItemPaddings,
} from '../DropdownItem.h'
import { DropdownItemTypeDivStyled } from './DropdownItemTypeDiv.styled'

export interface IDropdownItemTypeDivProps {
  title?: ITextBoxProps['title']
  id?: number
  activeId?: number
  font?: DropdownItemFonts
  disabled?: boolean
  width?: string
  height?: number
  paddings?: DropdownItemPadding
  columnGap?: DropdownItemColumnGap
  colorDefault?: DropdownItemFontColors
  colorHovered?: DropdownItemFontColors
  colorPressed?: DropdownItemFontColors
  colorSelected?: DropdownItemFontColors
  colorDisabled?: DropdownItemFontColors
  backgroundDefault?: DropdownItemBackgrounds
  backgroundHovered?: DropdownItemBackgrounds
  backgroundPressed?: DropdownItemBackgrounds
  backgroundSelectedDefault?: DropdownItemBackgrounds
  backgroundDisabledDefault?: DropdownItemBackgrounds
  icon?: IconNames
  iconSize?: IconSizes
  iconColor?: COLOR
  onClick?: (e: MouseEvent<HTMLDivElement>) => void
  children?: React.ReactNode
  selected?: boolean
}

export const DropdownItemTypeDiv = ({
  title = '',
  id,
  activeId,
  font = DropdownItemFonts.Caption4,
  disabled = false,
  width = '100%',
  height = 0,
  paddings = DropdownItemPaddings.SIZE_0,
  columnGap = DropdownItemColumnGap.INDENT_0,
  colorDefault,
  colorHovered = DropdownItemFontColors.TEXT_ICON_PRIMARY,
  colorPressed = DropdownItemFontColors.TEXT_ICON_PRIMARY,
  colorSelected = DropdownItemFontColors.TEXT_ICON_PRIMARY,
  colorDisabled = DropdownItemFontColors.TEXT_ICON_TERTIARY,
  backgroundDefault = DropdownItemBackgrounds.TRANSPARENT,
  backgroundHovered = DropdownItemBackgrounds.SURFACE_OPACITY_LIGHT_15,
  backgroundPressed = DropdownItemBackgrounds.SURFACE_OPACITY_LIGHT_20,
  backgroundSelectedDefault = DropdownItemBackgrounds.TRANSPARENT,
  backgroundDisabledDefault = DropdownItemBackgrounds.TRANSPARENT,
  icon,
  iconSize,
  iconColor,
  onClick,
  children,
  selected,
}: IDropdownItemTypeDivProps): JSX.Element => {
  const isSelected =
    selected ??
    ((Boolean(id) || id === 0) &&
      (Boolean(activeId) || activeId === 0) &&
      id === activeId)

  return (
    <DropdownItemTypeDivStyled
      font={font}
      selected={isSelected}
      disabled={disabled}
      width={width}
      height={height}
      paddings={paddings}
      columnGap={columnGap}
      colorDefault={colorDefault}
      colorHovered={colorHovered}
      colorPressed={colorPressed}
      colorSelected={colorSelected}
      colorDisabled={colorDisabled}
      backgroundDefault={backgroundDefault}
      backgroundHovered={backgroundHovered}
      backgroundPressed={backgroundPressed}
      backgroundSelectedDefault={backgroundSelectedDefault}
      backgroundDisabledDefault={backgroundDisabledDefault}
      onClick={onClick}
    >
      {icon && <Icon name={icon} size={iconSize} color={iconColor} />}
      {children ? (
        children
      ) : (
        <TextBox title={title} alignment={TextAligning.Center} maxLines={1} />
      )}
    </DropdownItemTypeDivStyled>
  )
}
